<template>
    <div ref="test-drive-form-success">
        <div v-if="!submissionSuccess" class="test-drive-form container">
            <div class="test-drive-form__details">
                <img class="img-responsive" :src="vehicleImage" alt="">
                <span class="test-drive-form__title">Book a personalised test drive</span>
                <p>
                    Please select a date and time which suits you at {{ vehicle.location.name }} and find out everything you
                    need to know about this {{ vehicle.manufacturer }} on a personalised test drive.
                </p>

                <div class="test-drive-form__sidebar-section">
                    <span class="test-drive-form__section-title test-drive-form__no-pad">Dealer address</span>
                    <p class="test-drive-form__no-pad">
                        {{ vehicle.location.name }} <br />
                        {{ vehicle.location.address }} <br />
                        {{ vehicle.location.postcode }}
                    </p>
                </div>
            </div>

            <div class="rd-card rd-card--border-top">
                <template v-if="loading">
                    <LoadingSpinner />
                </template>
                <template v-else>
                    <span class="test-drive-form__section-title">Please choose a date</span>
                    <div class="select-datetime__content--form">
                        <div class="select-datetime__content--input">
                            <div class="select-datetime__content--flatpickr">
                                <input type="text" name="date" ref="date" id="date"
                                    class="form-control form-control--select select-datetime__content--select-input"
                                    :value="formattedDate" placeholder="Please select a date" />
                                <button class="select-datetime__content--button-input" @click="toggleDateSelect"></button>
                            </div>
                        </div>
                    </div>

                    <span v-if="errors.date" class="form-error">
                        <template v-if="errors.time">
                            Please select a date and time
                        </template>
                        <template v-else>
                            Please select a date
                        </template>
                    </span>

                    <span v-if="dayTimes" class="test-drive-form__section-title">Please select an available time slot</span>
                    <div v-if="dayTimes" class="select-datetime__content--options">
                        <div v-for="(time, index) in dayTimes" :key="index" class="select-datetime__content--option"
                            :class="{ active: form.time === time }" @click="updateTime(time)">
                            <input type="radio" :id="time.split(' ').join('')" name="time" :value="time.split(' ').join('')"
                                class="select-datetime__content--radio" />
                            <label :for="time.split(' ').join('')">{{ time }}</label>
                        </div>
                    </div>

                    <span v-if="dayTimes && errors.time" class="form-error">
                        Please select a time
                    </span>

                    <span class="test-drive-form__section-title">Please enter your details</span>
                    <div class="form-grid form-grid--xs-1-column">
                        <div class="form-grid__col-span-2 form-grid__col--xs-full-width">
                            <div class="form-element__container">
                                <select v-model="form.title" name="title" data-validate="select"
                                    data-validate-message="Please select your title"
                                    class="form-element__input form-element__input--title">
                                    <option value="" disabled>Title*</option>
                                    <option value="Mr.">Mr.</option>
                                    <option value="Mrs.">Mrs.</option>
                                    <option value="Miss">Miss</option>
                                    <option value="Dr.">Dr.</option>
                                    <option value="Ms.">Ms.</option>
                                    <option value="Prof.">Prof.</option>
                                </select>
                            </div>

                            <span v-if="errors.title" class="form-error">
                                Please select your title
                            </span>
                        </div>

                        <div class="form-grid__col form-grid__col--xs-full-width">
                            <div class="form-element__container">
                                <input v-model="form.firstName" type="text" data-validate="input"
                                    data-validate-message="Please enter your first name" name="first-name"
                                    autocomplete="off" class="form-element__input">
                                <label :class="{
                                    'form-element__label': true,
                                    'form-element__label--hidden': form.firstName.length > 0
                                }">
                                    First Name *
                                </label>
                            </div>

                            <span v-if="errors.firstName" class="form-error">
                                Please enter your first name
                            </span>
                        </div>

                        <div class="form-grid__col form-grid__col--xs-full-width">
                            <div class="form-element__container">
                                <input v-model="form.lastName" type="text" data-validate="input"
                                    data-validate-message="Please enter your last name" name="last-name" autocomplete="off"
                                    class="form-element__input">
                                <label :class="{
                                    'form-element__label': true,
                                    'form-element__label--hidden': form.lastName.length > 0
                                }">
                                    Last Name *
                                </label>
                            </div>

                            <span v-if="errors.lastName" class="form-error">
                                Please enter your last name
                            </span>
                        </div>

                        <div class="form-grid__col form-grid__col--xs-full-width">
                            <div class="form-element__container email-container">
                                <input v-model="form.email" type="text" data-validate="email"
                                    data-validate-message="Please enter your email address" name="email" autocomplete="off"
                                    class="form-element__input email__input">
                                <label :class="{
                                    'form-element__label email__label': true,
                                    'form-element__label--hidden': form.email.length > 0
                                }">
                                    Email *
                                </label>
                            </div>

                            <span v-if="errors.email" class="form-error">
                                <template v-if="form.email.length > 0">
                                    Please enter a valid email address
                                </template>
                                <template v-else>
                                    Please enter your email address
                                </template>
                            </span>
                        </div>

                        <div class="form-grid__col form-grid__col--xs-full-width">
                            <div class="form-element__container">
                                <input v-model="form.telephone" type="tel" data-validate="tel"
                                    data-validate-message="Please enter a telephone number" name="telephone"
                                    autocomplete="off" class="form-element__input">
                                <label :class="{
                                    'form-element__label': true,
                                    'form-element__label--hidden': form.telephone.length > 0
                                }">
                                    Telephone *
                                </label>
                            </div>

                            <span v-if="errors.telephone" class="form-error">
                                Please enter a telephone number
                            </span>
                        </div>

                        <div class="form-grid__col-span-2 form-grid__col--xs-full-width">
                            <customer-privacy />

                            <span v-if="errors.privacyTerms" class="form-error">
                                You must agree to our terms & conditions
                            </span>

                            <span v-if="errors.marketingPreference" class="form-error">
                                Please let us know if you would like to hear from us by email or SMS
                            </span>
                        </div>

                        <button v-on:click="submitForm" class="btn btn--primary btn--xs-full-width">
                            Book a Test Drive
                        </button>
                    </div>
                </template>
            </div>
        </div>

        <div v-else class="container test-drive-form-success">
            <div class="rd-card rd-card--border-top">
                <h2>Thanks for your {{ vehicle.manufacturer }} {{ vehicle.model }} test drive request</h2>
                <p>
                    A member of the team will be in contact shortly to confirm your appointment.
                    If you would like to find out more about {{ vehicle.location.name }} or receive a free, instant
                    valuation for your current vehicle, please use the links below. We hope you enjoy your
                    {{ vehicle.manufacturer }} {{ vehicle.model }} test drive.
                </p>
                <div class="test-drive-form-success__button-group">
                    <a :href="`/${vehicle.location.slug}`" class="btn btn--primary btn--full-width-xs">
                        View Dealer
                    </a>
                    <a href="/car-valuation" class="btn btn--primary btn--full-width-xs">
                        Value My Car
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { isSameDay, isBefore, parse, setHours } from "date-fns";
import LoadingSpinner from '../../../core/components/LoadingSpinner.vue';

export default {
    props: {
        vehicle: {
            type: Object,
            required: true
        }
    },
    components: {
        LoadingSpinner,
    },
    mounted() {
        this.minDate = new Date().fp_incr(2),
        this.maxDate = new Date(this.minDate),
        this.maxDate.setMonth(this.maxDate.getMonth() + 6),
        this.getDealerClosedDays()
            .then(() => {
                this.$nextTick(() => {
                    this.initializeDatePicker();
                    this.initializeForm();
                })
            })
            .catch(error => {
                console.error(error);
                reject(error);
            });
    },
    data() {
        return {
            minDate: null,
            maxDate: null,
            flatpickrInstance: null,
            availableTimes: [],
            dayTimes: null,
            closedDays: [],
            form: {
                date: null,
                time: null,
                title: '',
                firstName: '',
                lastName: '',
                email: '',
                telephone: '',
                marketingPreference: '',
                privacyTerms: '',
            },
            errors: {
                date: false,
                time: false,
                title: false,
                firstName: false,
                lastName: false,
                email: false,
                telephone: false,
                marketingPreference: false,
                privacyTerms: false,
            },
            submissionAttempted: false,
            submissionSuccess: false,
            loading: false,
        }
    },
    methods: {
        initializeDatePicker() {
            this.flatpickrInstance = flatpickr(this.$refs.date, {
                minDate: this.minDate,
                maxDate: this.maxDate,
                altInput: false,
                dateFormat: "D J F Y",
                locale: {
                    firstDayOfWeek: 1, // start week on Monday
                },
                disableMobile: true,
                disable: [
                    date => {
                        if (this.closedDaysLength == 0) {
                            return false;
                        }
                        for (let index = 0; index < this.closedDaysLength; index++) {
                            if (isSameDay(this.closedDays[index], date)) {
                                return true;
                            }
                        }
                        return false;
                    },
                ],

                onChange: ([date]) => {
                    this.form.date = setHours(date, 12).toDateString();
                    // this.$store.commit("setDate", { date: date.toDateString() });
                    this.getDealerOpeningTimes();
                },
            });
        },

        parseDateStringToDate(dateString) {
            if (typeof dateString == 'Object') {
                return dateString;
            }
            if (!dateString) {
                return new Date();
            }
            return parse(dateString.split(' ') + ' 00:00:00');
        },

        toggleDateSelect() {
            this.flatpickrInstance.open();
        },

        getDealerClosedDays() {
            return new Promise((resolve, reject) => {
                axios({
                    method: "GET",
                    url: "/api/dealer-closed-days?dealer=" + this.vehicle.location.id + "&service_type=Sales",
                })
                .then((response) => {
                    const closedDays = response.data;
                    closedDays.forEach((closedDay, index) => {
                        closedDays[index] = parse(closedDay);
                    })
                    this.closedDays = closedDays;
                    resolve();
                })
                .catch((error) => {
                    console.error(error);
                    reject();
                });
            })
        },

        getDealerOpeningTimes() {
            axios.get('/api/dealer-opening-times', {
                params: {
                    dealer: this.vehicle.location.id,
                    service_type: 'Sales',
                    interval: '60 minutes',
                    date: this.form.date,
                }
            })
                .then((response) => {
                    this.dealerTimes = response.data;
                    this.dayTimes = this.dealerTimes;

                    if (this.isMounted) {
                        this.initializeDatePicker();
                    }
                })
                .catch((error) => {
                    console.error(error);
                });
        },

        updateTime(time) {
            this.form.time = time;
        },

        initializeForm() {
            document.querySelectorAll('input[name="marketing_preference"]').forEach((input) => {
                input.addEventListener('change', (event) => {
                    this.form.marketingPreference = event.target.value;
                });
            });

            document.querySelectorAll('input[name="privacy-terms"]').forEach((input) => {
                input.addEventListener('change', (event) => {
                    this.form.privacyTerms = event.target.checked
                });
            });
        },

        validateForm() {
            let pass = true;

            // all fields are required not null or empty
            for (const [key, value] of Object.entries(this.form)) {
                if (value === null || value === '') {
                    this.errors[key] = true;
                    pass = false;
                } else {
                    this.errors[key] = false;
                }
            }

            // privacy terms must be checked
            if (!this.form.privacyTerms) {
                this.errors.privacyTerms = true;
                pass = false;
            }

            // email must be valid
            if (!this.form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
                this.errors.email = true;
                pass = false;
            }

            return pass;
        },

        submitForm() {
            this.submissionAttempted = true;
            const pass = this.validateForm();

            if (!pass) {
                return;
            }

            reCaptchaProtect(() => {
                this.loading = true;
                // post via axios
                window.axios.post(`/api/used-vehicles/test-drive/${this.vehicle.id}`, {
                    'date': this.form.date,
                    'time': this.form.time,
                    'title': this.form.title,
                    'first-name': this.form.firstName,
                    'last-name': this.form.lastName,
                    'email': this.form.email,
                    'telephone': this.form.telephone,
                    'marketing_preference': this.form.marketingPreference,
                    'privacy_terms': this.form.privacyTerms,
                }).then((response) => {
                    this.submissionSuccess = true;
                    this.loading = false;

                    this.$nextTick(() => {
                        this.scrollTo('test-drive-form-success')
                    });
                    this.gaTag('Used Vehicle', 'Test Drive', 'Complete');
                    this.generateLeadEvent('used_test_drive');
                }).catch((error) => {
                    console.error(error);
                    this.loading = false;
                });
            });
        },

        scrollTo(target) {
            if (typeof target == "undefined" || typeof this.$refs[target] == "undefined") {
                return;
            }
            this.$refs[target].$el.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        },
    },
    computed: {
        closedDaysLength() {
            return this.closedDays.length;
        },
        formattedDate() {
            return this.form.date ? new Date(this.form.date).toDateString() : "";
        },
        vehicleImage() {
            return this.$store.state.BuyOnline.vehicleImage;
        }
    },

    watch: {
        form: {
            handler() {
                if (this.submissionAttempted) {
                    this.validateForm();
                }
            },
            deep: true,
        }
    }
}
</script>

<style lang="scss">
@import '../../../../sass/redesign/variables';

.test-drive-form {
    display: grid;
    grid-template-columns: 1fr;
    gap: 30px 0;
    padding-top: 45px;
    padding-bottom: 45px;

    @media (min-width: $screen-sm) {
        grid-template-columns: 347px minmax(0, 1fr);
        gap: 30px;
    }

    &__title {
        display: block;
        font-size: 20px;
        font-weight: 700;
        line-height: 1.2;
        margin-top: 22px;
        margin-bottom: 22px;
        color: #192A44;
    }

    &__section-title {
        display: block;
        font-size: 16px;
        font-weight: 700;
        line-height: 1.2;
        margin-top: 30px;
        margin-bottom: 15px;
        color: #192A44;

        &:first-of-type {
            margin-top: 0;
        }
    }

    &__sidebar-section {
        display: flex;
        flex-direction: column;
        background-color: #ECF9FF;
        border-radius: 10px;
        padding: 22px;

        .test-drive-form__section-title {
            margin-top: 0;
            margin-bottom: 6px;
        }
    }

    &__no-pad {
        margin: 0;
        padding: 0;
    }
}

.test-drive-form-success {
    padding-top: 45px;
    padding-bottom: 45px;

    &__button-group {
        display: flex;
        flex-direction: column;
        gap: 15px;

        @media (min-width: $screen-sm) {
            flex-direction: row;
        }
    }
}

.form-grid {
    margin-top: 15px;
}

.form-element__label {
    pointer-events: none;
}

.form-error {
    display: block;
    text-align: left;
    margin: 10px auto;
    padding: 10px;
    color: red;
    font-size: 16px;
    font-weight: 600;
}

.select-datetime {
    &__content {

        &--form {
            display: flex;
            justify-content: space-between;
            gap: 54px;

            @media (max-width: 800px) {
                flex-direction: column-reverse;
            }
        }

        &--input {
            width: 50%;

            @media (max-width: 800px) {
                width: 100%;
            }
        }

        &--flatpickr {
            position: relative;

            @media (max-width: 800px) {
                width: 100%;
            }
        }

        &--select-input {
            position: relative;
            background-color: #f7f7f7;
            border: none;
            outline: none;
            border-radius: 6px;
            font-size: 16px;

            @media (max-width: 800px) {
                width: 100%;
            }
        }

        &--button-input {
            position: absolute;
            top: 0;
            right: 0;
            width: 50px;
            height: 100%;
            background-image: url("/images/shared/glyphs/glyphicons-46-calendar.png");
            background-repeat: no-repeat;
            background-position: center;
            background-color: transparent;
            background-size: 50%;
            outline: none;
            border: none;
            opacity: 0.6;

            &:hover {
                cursor: pointer;
            }
        }

        &--options {
            display: grid;
            grid-template-columns: repeat(6, 1fr);
            column-gap: 10px;
            row-gap: 25px;
            padding-top: 25px;
            text-align: center;

            @media (max-width: 800px) {
                grid-template-columns: repeat(3, 1fr);
            }

            .active {
                background-color: #0093d6;
                color: #fff;
            }
        }

        &--option {
            padding: 10px;
            border-radius: 6px;
            border: 1px solid #0093d6;
            color: #0093d6;
            font-size: 16px;
            cursor: pointer;
            text-transform: capitalize;
            transition: 0.3s;

            &:hover {
                background-color: #0093d6;
                color: #fff;
            }
        }

        &--radio {
            display: none;
        }
    }
}
</style>


// WEBPACK FOOTER //
// resources/assets/js/used-vue/components/UsedDetails/TestDriveForm.vue